import { endOfDay } from 'date-fns'
import ms from 'ms'
import { CSSProperties } from 'react'

import { Button } from '@/components/ui/button'
import { isDevelopment } from '@/lib/helpers/envHelpers'
import { PanelStatusFilter } from '@/pages/professionalNetworkRelationships/hooks/useProfessionalNetworkFilters'
import { ReferralsTab } from '@/pages/referrals/constants'
import { ReferralsImportStep } from '@/redux/slices/referralsImportBase'
import { OrderDirection } from '@/types/api'
import {
  Breakpoint,
  CannotViewReferralReason,
  ClientPartyType,
  CommentEntityAlias,
  CommercialAgreementReferralDirection,
  CommercialAgreementReviewStatus,
  CommercialAgreementStatus,
  CommercialAgreementTemplatePlaceholder,
  CommercialAgreementType,
  CompanyDetailsIdentifierType,
  CompanyEmailType,
  CompanyProfileIdentifierType,
  CompanyRegister,
  CompanyStatus,
  CompanyTypeCode,
  CompassPdfReportSection,
  CompassReportStatus,
  ConsentDecisionType,
  ConsentType,
  DashboardFilters,
  DashboardReferralsChartDataSource,
  DashboardReferralsChartType,
  DoughnutChartSize,
  EmailContentType,
  EmailServiceType,
  EmbedCodeTemplateVariant,
  FcaType,
  FeeSharingType,
  FileUploadBoxState,
  IntellifloStatusType,
  LockedClientDetailsReason,
  MediaObjectContext,
  MeetingFormat,
  NormalReferralTypeOption,
  PanelInviteSource,
  PanelRequestApprovalStatus,
  PanelStatus,
  PendingPanelAction,
  PendingUserAction,
  PlannrConfigSection,
  PlannrStatusType,
  RecommendedPartnerActionType,
  ReferralAccessDeniedReason,
  ReferralApprovalStatus,
  ReferralArchiveState,
  ReferralCommissionConsent,
  ReferralConsentLetterVariant,
  ReferralConsentStage,
  ReferralDirection,
  ReferralDirectionCommercialAgreementStatus,
  ReferralImportDirection,
  ReferralLinkScheduler,
  ReferralLinkType,
  ReferralNoteType,
  ReferralSource,
  ReferralStage,
  ReferralStatusCode,
  ReferralTarget,
  ReferralType,
  ReferralTypeOption,
  RqCertifiedAssessmentStage,
  RqCertifiedAssessmentStatus,
  RqCustomEvent,
  SelectOption,
  StateKey,
  StateType,
  UserActionName,
  UserContextType,
} from '@/types/misc'
import { LocationUserType } from '@/types/responses/locations'

export const API_PATH_INTERNAL = '/internal/v1'

export const FEE_CATEGORY_OPTION_OTHER = 'OTHER'

// See https://tailwindcss.com/docs/responsive-design#overview
export const BREAKPOINT_DIMENSIONS: Record<Breakpoint, number> = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
}

// Tailwind's container-query breakpoints, in pixels (assuming a 16px root).
// Use these when matching a `@container` query (e.g. an `@2xl:` class) in JS,
// rather than the viewport BREAKPOINT_DIMENSIONS above.
// https://tailwindcss.com/docs/responsive-design#container-size-reference
export const CONTAINER_BREAKPOINTS = {
  '3xs': 256,
  '2xs': 288,
  xs: 320,
  sm: 384,
  md: 448,
  lg: 512,
  xl: 576,
  '2xl': 672,
  '3xl': 768,
  '4xl': 896,
  '5xl': 1024,
  '6xl': 1152,
  '7xl': 1280,
} as const

export const BREAKPOINTS: Record<Breakpoint, Breakpoint> = {
  sm: 'sm',
  md: 'md',
  lg: 'lg',
  xl: 'xl',
  '2xl': '2xl',
}

export const REFERRAL_DIRECTION_COMMERCIAL_AGREEMENT_STATUS: Record<
  ReferralDirectionCommercialAgreementStatus,
  ReferralDirectionCommercialAgreementStatus
> = {
  CURRENT_AGREEMENT_AWAITING_REVIEW_BY_US:
    'CURRENT_AGREEMENT_AWAITING_REVIEW_BY_US',
  CURRENT_AGREEMENT_AWAITING_REVIEW_BY_THEM:
    'CURRENT_AGREEMENT_AWAITING_REVIEW_BY_THEM',
  PENDING_AGREEMENT_AWAITING_REVIEW_BY_US:
    'PENDING_AGREEMENT_AWAITING_REVIEW_BY_US',
  PENDING_AGREEMENT_AWAITING_REVIEW_BY_THEM:
    'PENDING_AGREEMENT_AWAITING_REVIEW_BY_THEM',
  COMPLETE: 'COMPLETE',
}

export const COMMERCIAL_AGREEMENT_STATUS: Record<
  CommercialAgreementStatus,
  CommercialAgreementStatus
> = {
  CURRENT: 'CURRENT',
  PENDING: 'PENDING',
}

export const COMMERCIAL_AGREEMENT_REVIEW_STATUS: Record<
  CommercialAgreementReviewStatus,
  CommercialAgreementReviewStatus
> = {
  AWAITING_REVIEW_BY_US: 'AWAITING_REVIEW_BY_US',
  AWAITING_REVIEW_BY_THEM: 'AWAITING_REVIEW_BY_THEM',
  COMPLETE: 'COMPLETE',
}

export const FEE_SHARING_TYPE = {
  percentage: 'PERCENTAGE',
  setAmount: 'SET_AMOUNT',
} satisfies Record<string, FeeSharingType>

export const SERVICE_FEE_CATEGORY_TYPES = {
  initialFees: {
    label: 'Initial fees',
    value: 'INITIAL_FEE',
  },
  ongoingFees: {
    label: 'Ongoing fees',
    value: 'ONGOING_FEE',
  },
  arrangementFee: {
    label: 'Arrangement fee',
    value: 'ARRANGEMENT_FEE',
  },
  totalFee: {
    label: 'Total fees',
    value: 'TOTAL_FEE',
  },
  other: {
    label: 'Other - please specify',
    value: FEE_CATEGORY_OPTION_OTHER,
  },
}

export const SERVICE_FEE_SHARE_TYPE = {
  fixedFee: {
    label: 'Fixed Fee',
    value: FEE_SHARING_TYPE.setAmount,
  },
  percentage: {
    label: 'Percentage',
    value: FEE_SHARING_TYPE.percentage,
  },
}

export const SERVICE_FEE_DURATION = {
  lifeOfTheContract: '-1',
}

export const SERVICE_FEE_DURATION_OPTIONS: SelectOption[] = [
  {
    label: 'The life of the contract',
    value: SERVICE_FEE_DURATION.lifeOfTheContract,
  },
  { label: '1 year', value: '1' },
  { label: '2 years', value: '2' },
  { label: '3 years', value: '3' },
  { label: '4 years', value: '4' },
  { label: '5 years', value: '5' },
  { label: '6 years', value: '6' },
  { label: '7 years', value: '7' },
  { label: '10 years', value: '10' },
]

export const LAYOUT = {
  FLUID: 'fluid',
  BOXED: 'boxed',
}

export const THEME = {
  DEFAULT: 'default',
  COLORED: 'colored',
  DARK: 'dark',
  LIGHT: 'light',
}

/** @todo migrate to CSS variables. E.g. var(--primary) */
export const THEME_PALETTE_LIGHT = {
  'primary-dark': '#1659c7',
  'primary-light': '#84aef2',
  secondary: '#495057',
  successDark: '#99D7AE',
  successLight: '#DDF2E4',
  successExtraLight: '#DDF2E4',
  orange: '#f0ad4e',
  warningLight: '#FFD198',
  warningExtraLight: '#FFFBF6',
  red: '#d9534f',
  redExtraLight: '#FFE0DC',
  redLight: '#FFA295',
  tableStripedBg: '#f4f7f9',
  destructiveLight: '#FF5353',
  destructiveExtraLight: '#d88c8c',
  purple: '#7F4EBED9',
  white: '#fff',
  gray: '#F7F8FA',
  '#D5D5D5': '#D5D5D5',
  grayBox: '#F8F8F8',
  grayLight: '#D1D4D7',
  'gray-100': '#f4f7f9',
  'gray-200': '#e2e8ee',
  'gray-300': '#dee6ed',
  'gray-400': '#ced4da',
  'gray-500': '#adb5bd',
  'gray-600': '#6c757d',
  'gray-700': '#495057',
  'gray-800': '#020202',
  'gray-900': '#212529',
  statLabelColor: '#b6b6b6',
  black: '#000',
  yellowDocuSign: '#ffc820',
  fontWeights: {
    semiBold: 500,
    bold: 700,
  },
}

/** @todo migrate to CSS variables. */
export const CHART_PALETTE = {
  blue: '#46a1f8',
  yellow: '#ffa600',
  blueDark: '#2f79c0',
  orange: '#EC7A08',
  red: '#F8333C',
  pink: '#fb8489',
  pinkDark: '#bc5090',
  purple: '#58508d',
  green: '#89be28',
  greyLight: 'rgb(215,215,215)',
  yellowDark: '#e6d000',
  greenDark: '#6b941f',
  greyDark: 'rgba(107, 107, 107, 1)',
}

/** @todo migrate to CSS variables. */
export const COMPASS_PDF_REPORT_PALETTE = {
  blue: '#06B0F0',
}

/** @todo migrate to CSS variables. */
export const THEME_PALETTE_DARK = {
  ...THEME_PALETTE_LIGHT,
  'primary-dark': '#84aef2',
  'primary-light': '#1659c7',
  white: '#293042',
  'gray-100': '#3e4555',
  'gray-200': '#545968',
  'gray-300': '#696e7b',
  'gray-400': '#7f838e',
  'gray-500': '#9498a1',
  'gray-600': '#a9acb3',
  'gray-700': '#bfc1c6',
  'gray-800': '#d4d6d9',
  'gray-900': '#eaeaec',
  black: 'black',
  dangerAlternate: '#FF644F',
}

export const COMPASS_REPORT_STATUS = {
  INVITED: 'INVITED',
  COMPLETE: 'COMPLETE',
  IN_PROGRESS: 'IN_PROGRESS',
} satisfies Record<CompassReportStatus, CompassReportStatus>

export const CONSENT_TYPE: Record<ConsentType, ConsentType> = {
  ICAEW: 'ICAEW',
}
export const CONSENT_DECISION: Record<
  ConsentDecisionType,
  ConsentDecisionType
> = {
  OPT_IN: 'OPT_IN',
  OPT_OUT: 'OPT_OUT',
}

export const COMPANY_PROFILE_IDENTIFIER_TYPE: Record<
  CompanyProfileIdentifierType,
  CompanyProfileIdentifierType
> = {
  frn: 'frn',
  sraNumber: 'sraNumber',
  companyId: 'companyId',
}

export const COMPANY_DETAILS_IDENTIFIER_TYPE: Record<
  CompanyDetailsIdentifierType,
  CompanyDetailsIdentifierType
> = {
  companyId: 'companyId',
  referralCode: 'referralCode',
}

export const COMPANY_STATUS: Record<CompanyStatus, CompanyStatus> = {
  SUSPENDED: 'SUSPENDED',
  CLAIMED: 'CLAIMED',
}

export const COMPANY_TYPE_CODE: Record<CompanyTypeCode, CompanyTypeCode> = {
  ifa: 'ifa',
  solicitor: 'solicitor',
  accountant: 'accountant',
  mortgage_adviser: 'mortgage_adviser',
  business_lending_non_fca: 'business_lending_non_fca',
  insurance_adviser: 'insurance_adviser',
  corporate_finance: 'corporate_finance',
  corporate_finance_non_fca: 'corporate_finance_non_fca',
  investment_management: 'investment_management',
  auto_enrolment: 'auto_enrolment',
  business_coach: 'business_coach',
  conveyancing: 'conveyancing',
  employee_benefits_provider: 'employee_benefits_provider',
  estates_admin: 'estates_admin',
  estates_agent: 'estates_agent',
  estate_planner: 'estate_planner',
  management_consultant: 'management_consultant',
  marketing_consultant: 'marketing_consultant',
  property_developer: 'property_developer',
  software_provider: 'software_provider',
  surveyor: 'surveyor',
  venture_capital: 'venture_capital',
  other: 'other',
}

export const COMPANY_TYPE_ID: Partial<
  Record<keyof typeof COMPANY_TYPE_CODE, number>
> = {
  ifa: 1,
}

export const COMPANY_TYPE_EMAIL: Record<CompanyEmailType, CompanyEmailType> = {
  COMPASS_INVITE: 'COMPASS_INVITE',
}

export const COMMERCIAL_AGREEMENT_TYPE: Record<
  CommercialAgreementType,
  CommercialAgreementType
> = {
  NO: 'NO',
  YFS: 'YFS',
  YJV: 'YJV',
  RQP: 'RQP',
}

export const COMMERCIAL_AGREEMENT_REFERRAL_DIRECTION: Record<
  CommercialAgreementReferralDirection,
  CommercialAgreementReferralDirection
> = {
  INCOMING: 'INCOMING',
  OUTGOING: 'OUTGOING',
}

export const EMAIL_ADDRESSES = {
  rqHello: 'hello@rq.app',
  rqSupport: 'support@rq.app',
}

export const EMAIL_SERVICE_TYPE: Record<EmailServiceType, EmailServiceType> = {
  RQ: 'RQ',
  SMTP: 'SMTP',
  NYLAS: 'NYLAS',
}

export const EMAIL_CONTENT_TYPE: Record<EmailContentType, EmailContentType> = {
  RQ: 'RQ',
  PERSONAL: 'PERSONAL',
  COMPANY: 'COMPANY',
}

export const RQ_CERTIFIED_ASSESSMENT_STATUS_TYPE = {
  notStarted: 'NOT_STARTED',
  pendingPaymentConfirmation: 'PENDING_PAYMENT_CONFIRMATION',
  inProgress: 'IN_PROGRESS',
  inReview: 'IN_REVIEW',
  passed: 'PASSED',
  failed: 'FAILED',
} satisfies Record<string, RqCertifiedAssessmentStatus>

export const RQ_CERTIFIED_ASSESSMENT_STAGE = {
  one: 'one',
  two: 'two',
} satisfies Record<RqCertifiedAssessmentStage, RqCertifiedAssessmentStage>

/**
 * @see https://date-fns.org/docs/format for date formats.
 */
export const DATE_FORMATS = {
  DAY_MONTH_YEAR: 'dd/LL/yyyy', // 05/03/2002
  DAY_MONTH_YEAR_TIME: 'dd/LL/yyyy, h:mm a', // 05/03/2002, 2:53 PM
  MONTH_YEAR: 'LL/yyyy', // 05/2002
  TIME: 'h:mmaaa', // '2:53pm'
  YEAR_MONTH_DAY: 'yyyy-LL-dd', // 2002-05-03
  YEAR_MONTH_DAY_TIME: 'yyyy-LL-dd H:mm', // 2002-05-03 14:53
  MONTH_DAY_YEAR: 'MMM d, yyyy', // May 3, 2002
  MONTH_DAY_YEAR_TIME: 'MMM d, yyyy h:mm a', // May 3, 2002 2:53 PM
  /**
   * Like LONG_DAY_MONTH_YEAR_TIME, but without the ordinal suffix on the day (e.g. "3rd" vs "3").
   */
  MEDIUM_DAY_MONTH_YEAR_TIME: 'd MMMM yyyy h:mm a', // 3 November 2002 2:52 PM
  LONG_DAY_MONTH_YEAR: 'do MMMM yyyy', // 3rd November 2002
  LONG_DAY_MONTH_YEAR_TIME: 'do MMMM yyyy h:mm a', // 3rd November 2002, 2:52 PM
  SHORT_DAY_MONTH_YEAR: 'd MMM yyyy', // 3 Nov 2002
  SHORT_DAY_MONTH_YEAR_TIME: "d MMM yyyy 'at' HH:mm", // 3 Nov 2002 at 14:53
  SHORTEST_DAY_MONTH_YEAR: 'dd/LL/yy', // 03/11/02
}

export const ERROR_MESSAGES = {
  genericServerError:
    'Sorry but there was a problem on our side. ' +
    'Our team has been notified and will attempt to fix the issue as soon ' +
    'as possible. Please try again later.',
  required: 'This field is required',
  blacklistedEmail:
    'That looks like a personal email account. Please enter a business email.',
}

export const FCA_MONITORING_CODE = {
  CompaniesHouse: 'CompaniesHouse',
  FCA: 'FCA',
  FOS: 'FOS',
  FSCS1: 'FSCS1',
  FSCS2: 'FSCS2',
}

export const ORDER_DIRECTION: Record<OrderDirection, OrderDirection> = {
  asc: 'asc',
  desc: 'desc',
}

export const FCA_SERVICE_TYPE_NAMES = {
  independent: 'Independent (Whole of market)',
  restrictedNotWholeOfMarket: 'Restricted - Not whole of market',
  restrictedWholeOfMarket: 'Restricted - Whole of market',
  no: 'No',
}

export const LOCAL_STORAGE_KEYS = {
  apiCommitHash: 'apiCommitHash',
  isProgrammaticReload: 'isProgrammaticReload',
  previouslySelectedScope: 'previouslySelectedScope',
  previouslySelectedCompanyId: 'previouslySelectedCompanyId', // Only read this when refreshing the page to pre-select company
  isDesktopSidebarExpanded: 'isDesktopSidebarExpanded',
  showDevLogo: 'showDevLogo',
}

export const PANEL_STATUS: Record<PanelStatus, PanelStatus> = {
  requested: 'requested',
  approved: 'approved',
  rejected: 'rejected',
}

export const PANEL_STATUS_FILTER: Record<PanelStatusFilter, PanelStatusFilter> =
  {
    all: 'all',
    approvedOrRequested: 'approvedOrRequested',
    requested: 'requested',
    approved: 'approved',
    rejected: 'rejected',
  }

export const PENDING_PANEL_ACTION: Record<
  PendingPanelAction,
  PendingPanelAction
> = {
  respondToInvite: 'respondToInvite',
  chaseInvite: 'chaseInvite',
  addCommercialAgreement: 'addCommercialAgreement',
  reviewCurrentAgreement: 'reviewCurrentAgreement',
  chaseCurrentAgreementReview: 'chaseCurrentAgreementReview',
  reviewPendingAgreement: 'reviewPendingAgreement',
  chasePendingAgreementReview: 'chasePendingAgreementReview',
  reviewCurrentIncomingAgreementConsentLetter:
    'reviewCurrentIncomingAgreementConsentLetter',
  reviewPendingIncomingAgreementConsentLetter:
    'reviewPendingIncomingAgreementConsentLetter',
}

export const COOKIES = {
  referringCompanyReferralCode: 'rq.referring_company_referral_code',
}

const DUMMY_CALENDLY_MEETING_LINK = 'https://calendly.com/sajadt'

export const EXTERNAL_LINKS = {
  dpbLicence:
    'https://www.icaew.com/regulation/dpb-investment-business-licence',
  marketingWebsite: 'https://rq.app',
  outlookAddIn:
    'https://appsource.microsoft.com/en-us/product/office/WA200007419?tab=Overview',
  calendly: {
    accountSetup: isDevelopment()
      ? DUMMY_CALENDLY_MEETING_LINK
      : 'https://calendly.com/isabel-rq/rq-account-config',
    officeHours: isDevelopment()
      ? DUMMY_CALENDLY_MEETING_LINK
      : 'https://calendly.com/isabel-rq/office-hours',
  },
  faqs: 'https://rq.app/resources/faqs',
  quickStartGuides: {
    index: 'https://rq.app/resources/quick-start-guides',
    sendingYourFirstReferral:
      'https://rq.app/resources/quick-start-guides/sending-your-first-referral',
    managingPartners:
      'https://rq.app/resources/quick-start-guides/managing-partners',
    invitingYourTeam:
      'https://rq.app/resources/quick-start-guides/inviting-your-team',
  },
  privacyPolicy: 'https://rq.app/privacy-policy',
  bookDemo: 'https://rq.app/demo',
  vimeo: {
    inviteToRq: 'https://player.vimeo.com/video/818424533?h=806c1e01ef',
    makeReferral: 'https://player.vimeo.com/video/818431321?h=e4ba16bd74',
    manageReferrals: 'https://player.vimeo.com/video/818422648?h=9ed2453ad9',
    manageRelationships:
      'https://player.vimeo.com/video/818423647?h=762c07dac0',
    compass: 'https://player.vimeo.com/video/818428419?h=5dec9b0238',
  },
}

export const FCA_ACCREDITATIONS = [
  { code: 'CISI', name: 'CISI Accredited' },
  { code: 'CII', name: 'CII Chartered' },
  { code: 'BCorp', name: 'BCorp' },
  { code: 'CEFEX', name: 'CEFEX' },
]

export const FCA_INDIVIDUAL_QUALIFICATIONS = [
  { code: 'Resolution', name: 'Resolution' },
  { code: 'SOLLA', name: 'SOLLA' },
  { code: 'STEP', name: 'STEP' },
  { code: 'STEP TEP', name: 'STEP TEP' },
  { code: 'STEP Full Member', name: 'STEP full member' },
]

export const RECOMMENDED_PARTNER_ACTION_TYPE: Record<
  RecommendedPartnerActionType,
  RecommendedPartnerActionType
> = {
  DISMISS: 'DISMISS',
  ACCEPT_WITH_TERMS: 'ACCEPT_WITH_TERMS',
  ACCEPT_WITHOUT_TERMS: 'ACCEPT_WITHOUT_TERMS',
}

export const REFERRAL_TYPE: Record<ReferralType, ReferralType> = {
  direct: 'direct',
  indirect: 'indirect',
}

export const REFERRAL_TYPE_OPTION: Record<
  ReferralTypeOption,
  ReferralTypeOption
> = {
  direct: 'direct',
  directCc: 'directCc',
  directPco: 'directPco',
  indirect: 'indirect',
  bookCallForClient: 'bookCallForClient',
  affiliateLink: 'affiliateLink',
}

export const REFERRAL_APPROVAL_STATUS: Record<
  ReferralApprovalStatus,
  ReferralApprovalStatus
> = {
  approved: 'approved',
  pending: 'pending',
  rejected: 'rejected',
}

export const NORMAL_REFERRAL_TYPE_OPTION: Record<
  NormalReferralTypeOption,
  NormalReferralTypeOption
> = {
  direct: 'direct',
  directCc: 'directCc',
  directPco: 'directPco',
  indirect: 'indirect',
  affiliateLink: 'affiliateLink',
}

export const REFERRAL_STATUS_ID = {
  PENDING: 1,
  AWAITING_CLIENT_TO_SCHEDULE_MEETING: 2,
  AWAITING_ADVISER_TO_CONTACT_CLIENT: 3,
  AWAITING_CLIENT_CONSENT: 50,
  INITIAL_MEETING_SCHEDULED: 450,
  COMPLETE: 500,
}

export const REFERRAL_STATUS_CODE: Record<
  ReferralStatusCode,
  ReferralStatusCode
> = {
  form_submission: 'form_submission',
  awaiting_client_consent: 'awaiting_client_consent',
  awaiting_client_meeting: 'awaiting_client_meeting',
  awaiting_client_schedule_meeting: 'awaiting_client_schedule_meeting',
  complete: 'complete',
  initial_meeting_held: 'initial_meeting_held',
  initial_meeting_scheduled: 'initial_meeting_scheduled',
  live: 'live',
  won_completed: 'won_completed',
  not_progressing: 'not_progressing',
  unable_contact_client: 'unable_contact_client',
  awaiting_client_response: 'awaiting_client_response',
  client_details_passed_to_pco: 'client_details_passed_to_pco',
  client_postponed_decision: 'client_postponed_decision',
  reconnect_in_future: 'reconnect_in_future',
  no_recent_activity: 'no_recent_activity',
  still_considering_options: 'still_considering_options',
  not_sure: 'not_sure',
}

export const REFERRAL_STAGE_CODE: Record<ReferralStage, ReferralStage> = {
  referred: 'referred',
  engaging: 'engaging',
  converting: 'converting',
  closed_won_recurring: 'closed_won_recurring',
  closed_won_one_off: 'closed_won_one_off',
  stalled: 'stalled',
  closed_lost: 'closed_lost',
  not_sure: 'not_sure',
}

export const REFERRAL_STAGE_LABEL: Record<ReferralStage, string> = {
  referred: 'Referred',
  engaging: 'Engaging',
  converting: 'Converting',
  closed_won_recurring: 'Closed - won, recurring',
  closed_won_one_off: 'Closed - won, one off',
  stalled: 'Stalled',
  closed_lost: 'Closed - lost',
  not_sure: 'Not sure',
}

export const REFERRAL_STAGE_MAX_STATUSES = 100

export const SERVICE_AREA_ID = {
  creationOfAFinancialPlan: 6,
  generalEnquiry: 1000,
  will: 1070,
}

export const SERVICE_AREA_NAME = {
  generalEnquiry: 'General Enquiry',
}

export const MAX_FILE_SIZE_BYTES = 20000000

// Max range for MySQL integer types is 2147483647
// https://tinyurl.com/mysql-integer-types
export const MAX_INTEGER_VALUE = 2000000000

// Assuming we're using the MySQL DECIMAL(10,2) type for money columns
export const MAX_MONEY_VALUE = 1000000000

export const STATE_KEY = {
  hasInteractedWithFirstRelationshipRequestApprovedModal:
    'interacted_with_first_relationship_request_approved_modal',
  hasWatchedDemoVideo: 'watched_demo_video',
  hasInteractedWithRqCertifiedAnnouncement:
    'interacted_with_rq_certified_announcement',
  hasInteractedWithWalkthroughBanner: 'interacted_with_walkthrough_banner',
  hasInteractedWithIntellifloIntegrationAutosaveNotice:
    'interacted_with_intelliflo_integration_autosave_notice',
  hasInteractedWithIcaewPricingModal: 'interacted_with_icaew_pricing_modal',
  hasInteractedWithPlannrIntegrationAutosaveNotice:
    'interacted_with_plannr_integration_autosave_notice',
  hasInteractedWithEmailAndCalendarConnectionPrompt:
    'interacted_with_email_and_calendar_connection_prompt',
  hasInteractedWithClaimReferralModal: 'interacted_with_claim_referral_modal',
  hasClientReviewedPartners: 'client_reviewed_partners',
  panelApprovedNew: 'panel_approved_new',
  panelApprovedUpdated: 'panel_approved_updated',
  panelRequestedNew: 'panel_requested_new',
  panelRequestedUpdated: 'panel_requested_updated',
  panelRequestPending: 'panel_request_pending',
  referralFromNew: 'referral_from_new',
  referralFromUpdated: 'referral_from_updated',
  referralToNew: 'referral_to_new',
  referralToUpdated: 'referral_to_updated',
  referralApprovalRequestNew: 'referral_approval_request_new',
  relationshipOnboardingModal: 'relationship_onboarding_modal',
} satisfies Record<string, StateKey>

export const THEME_PALETTE = THEME_PALETTE_LIGHT

export const STATE_TYPE: Record<StateType, StateType> = {
  user: 'user',
  company: 'company',
  companyUser: 'companyUser',
  group: 'group',
  groupUser: 'groupUser',
}

export const LOCATION_USER_TYPE: Record<LocationUserType, LocationUserType> = {
  companyUser: 'companyUser',
  inviteUser: 'inviteUser',
}

export const COMMON_DATA_ERROR_MESSAGE =
  'Common data endpoint returned empty results'

export const MONTH_OPTION_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

export const FCA_TYPE: Record<FcaType, FcaType> = {
  appointed_representative: 'appointed_representative',
  directly_authorised: 'directly_authorised',
  trading_name: 'trading_name',
}

export const DOUGHNUT_CHART_SIZE: Record<DoughnutChartSize, number> = {
  sm: 100,
  md: 140,
}

export const COMPASS_REPORT_PAGE_NAMES = {
  cover: 'Cover',
  tableOfContents: 'Table of contents',
  introduction: 'Introduction',
}

export const PDF_DIMENSIONS = {
  A4: {
    landscapeWidth: '297mm',
    landscapeHeight: '210mm',
    portraitWidth: '210mm',
    portraitHeight: '297mm',
  },
}
export const IMPORT_FILE_NOTIFICATION_DURATION = ms('7s')
export const IMPORT_REFERRALS_FILE_NOTIFICATION_DURATION = ms('2s')

export const COMPASS_REPORT_SECTION = {
  one: '1',
  two: '2',
  three: '3',
  four: '4',
} satisfies Record<string, CompassPdfReportSection>

export const COMPASS_PALETTE = {
  orange: '#F18F01',
  turquoise: '#81D2C7',
  blue: '#06B0F0',
}

export const FONT_SIZE_BASE = 13.2

export const FONT_SIZE_XS = 12

export const LONG_MESSAGE_DURATION = 7000

export const REGULATOR_CODE = {
  icaew: 'ICAEW',
  acca: 'ACCA',
  icas: 'ICAS',
  aat: 'AAT',
  sra: 'SRA',
  ls: 'LS',
  other: 'OTHER',
}

export const REGULATOR_ID = {
  aat: 12,
  other: 100,
}

export const API_VIOLATION_CODE = {
  attachmentSize: 'app-attachment-size',
  cannotUpdateReferralToCompany: 'app-cannot-update-referral-to-company',
  cannotDeleteService: 'app-cannot-delete-service',
  invalidEnumValue: 'invalid-enum-value',
  missingCommercialTerms: 'app-commercial-agreement-no-service-fee',
  missingReferralLinkIndividuals: 'app-missing-referral-link-individuals',
  unapprovedOrUnamendedConsentLetter:
    'app-commercial-agreement-unreviewed-consent-letter',
  referralNonStandaloneService: 'app-referral-non-standalone-service',
  referralLinkNonStandaloneService: 'app-referral-link-non-standalone-service',
  ukPostcodeInvalid: 'uk-postcode-invalid',
  relationshipExists: 'app-relationship-exists',
  relationshipRejected: 'app-relationship-rejected',
  dependantServices: 'app-dependant-services',
  meetingTypeHasReferralLinks: 'app-meeting-type-has-referral-links',
  meetingTypeIsLast: 'app-meeting-type-is-last',
  meetingTypeIsDefault: 'app-meeting-type-is-default',
  // Refuses deleting a company user who is still assigned to a referral page
  // (a meeting type internally — the code follows the entity name, the copy
  // follows the product term). The API-side constraint is commented to keep
  // this constant in step, so don't rename either half.
  companyUserAssignedToMeetingTypes:
    'app-company-user-assigned-to-meeting-types',
}

export const RQ_DETAILS = {
  companyNumber: '13202073',
  address: 'V117, Vox Studios, 1-45 Durham Street, London, SE11 5JH',
}

export const LOCATION_STATE = {
  skipNavigationPrompt: 'skipNavigationPrompt',
}

export const EXISTING_AGREEMENT_MAX_FILE_SIZE_MB = 20

export const POSTHOG_EVENT = {
  compassBulkInviteError: 'Compass bulk invite error',
  login: 'Login',
  logout: 'Logout',
  errorMessageShownToUser: 'Error message shown to user',
  sessionConflictShownToUser: 'Session conflict shown to user',
  referralLinkNotWorking: 'Calendar link not working',
  switchGroup: 'Switch Group',
  switchNetwork: 'Switch Network',
  switchCompany: 'Switch Company',
  switchClient: 'Switch Client',
}

// TODO: Add the other app areas here
export const POSTHOG_APP_AREA = {
  addCompany: 'add-company',
  completeRegistrationModal: 'complete-registration-modal',
}

export const DOM_IDS = {
  datepickerPortal: 'js-datepicker-portal',
  directLeadCaptureSuccessMessage: 'js-direct-lead-capture-success-message',
  logReferralErrorBox: 'js-log-referral-error-box',
  reviewConsentLetterBox: 'js-review-consent-letter-box',
  reactSelectPortal: 'js-react-select-portal',
  serviceFeeErrors: 'js-service-fee-errors',
  preferences: {
    privacyPolicy: 'js-preferences-privacy-policy',
  },
}

export const ZERO_TO_HUNDRED_OPTIONS = [
  '0-10',
  '10-20',
  '20-30',
  '30-40',
  '40-50',
  '50-60',
  '60-70',
  '70-80',
  '80-90',
  '90-100',
]

export const COMPANY_ADMIN_ROLE_IRI = `${API_PATH_INTERNAL}/roles/2`
export const COMPANY_PAYMENT_ADMIN_ROLE_IRI = `${API_PATH_INTERNAL}/roles/12`
export const COMPANY_MEETINGS_ADMIN_ROLE_IRI = `${API_PATH_INTERNAL}/roles/14`
export const GROUP_ADMIN_ROLE_IRI = `${API_PATH_INTERNAL}/roles/11`
export const GROUP_PAYMENT_ADMIN_ROLE_IRI = `${API_PATH_INTERNAL}/roles/13`
// Every network user holds the Network User role; admins also hold Network Admin
export const NETWORK_USER_ROLE_IRI = `${API_PATH_INTERNAL}/roles/15`
export const NETWORK_ADMIN_ROLE_IRI = `${API_PATH_INTERNAL}/roles/16`

export const ROLE_ID = {
  companyUserAdmin: 2,
  groupUserAdmin: 11,
}

export interface UserPermissionOption {
  label: string
  value: IRI[]
  tooltip?: string
}

export const GROUP_USER_PERMISSION_OPTIONS: UserPermissionOption[] = [
  { label: 'Member', value: [] },
  { label: 'Admin', value: [GROUP_ADMIN_ROLE_IRI] },
  {
    label: 'Payments Admin',
    value: [GROUP_PAYMENT_ADMIN_ROLE_IRI],
    tooltip:
      'Where you have fee-sharing agreements enabled through RQ, this individual will be responsible for confirming and approving payments/receipts.',
  },
]

export const COMPANY_USER_PERMISSION_OPTIONS: UserPermissionOption[] = [
  {
    label: 'Member',
    value: [],
    tooltip:
      'Access your professional network, but referrals and compass snapshots may be restricted to those you have created.',
  },
  {
    label: 'Admin',
    value: [COMPANY_ADMIN_ROLE_IRI],
    tooltip:
      'Access your professional network, referrals, and compass snapshots.',
  },
  {
    label: 'Meetings Admin',
    value: [COMPANY_MEETINGS_ADMIN_ROLE_IRI],
    tooltip:
      'This individual can manage calendar settings for all users and all calendar booking links.',
  },
  {
    label: 'Payments Admin',
    value: [COMPANY_PAYMENT_ADMIN_ROLE_IRI],
    tooltip:
      'Where you have fee-sharing agreements enabled through RQ, this individual will be responsible for confirming and approving payments/receipts.',
  },
]

export const COMPASS_NET_WORTH_PALETTE = [
  COMPASS_PALETTE.blue,
  COMPASS_PALETTE.turquoise,
  '#357DED',
  COMPASS_PALETTE.orange,
  ...Object.values(CHART_PALETTE),
]

export const NUM_COMPANY_REVIEWS_PER_PAGE = 3
export const NUM_COMPANY_INTEGRATION_LOGS_PER_PAGE = 5

export const COMMON_ACCOUNTANT_REGULATOR_CODES = [
  REGULATOR_CODE.icaew,
  REGULATOR_CODE.acca,
  REGULATOR_CODE.icas,
]

export const SRA_SOLICITOR_REGULATOR_CODES = [
  REGULATOR_CODE.ls,
  REGULATOR_CODE.sra,
]

export const NO_REGULATOR_VALUE = 'no-regulator'

export const OTHER_REGULATOR_VALUE = REGULATOR_ID.other.toString()

export const SHOW_MORE_REGULATORS_VALUE = 'show-more'

// export const COMPANY_DASHBOARD_SIDEBAR_LINKS = {
//   yourNetwork: 'Your network',
//   yourClients: 'Your clients',
//   compass: 'Compass',
//   referrals: 'Referrals',
//   referYourClient: 'Refer your client',
// }

export const CLIENT_DASHBOARD_SIDEBAR_LINKS = {
  compassDashboard: 'Dashboard',
  yourSnapshots: 'Your Snapshots',
  actions: 'Actions',
  startCompass: 'Start Compass',
  continueCompass: 'Continue Compass',
  ourPartners: 'Our Partners',
  updateCompass: 'Update Compass',
  sharing: 'Sharing',
  howItWorks: 'How it works',
}

export const GROUP_DASHBOARD_SIDEBAR_LINKS = {
  dashboard: 'Dashboard',
  overview: 'Group Overview',
  referrals: 'Referrals',
  stats: 'Stats',
  teamManagement: 'Team',
}

export const NETWORK_DASHBOARD_SIDEBAR_LINKS = {
  overview: 'Overview',
  dashboard: 'Dashboard',
  referralRegister: 'Referral register',
  users: 'Users',
  settings: 'Settings',
}

export const REFERRALS_DATA_STALE_TIME = ms('2m')

export const REFERRALS_TAB: Record<ReferralsTab, ReferralsTab> = {
  sent: 'sent',
  received: 'received',
  detected: 'detected',
}

export const REFERRAL_ARCHIVE_STATE: Record<
  ReferralArchiveState,
  ReferralArchiveState
> = {
  active: 'active',
  archived: 'archived',
  all: 'all',
}

export const ARCHIVE_STATE_OPTIONS: Array<{
  value: ReferralArchiveState
  label: string
}> = [
  { value: REFERRAL_ARCHIVE_STATE.active, label: 'Active referrals' },
  { value: REFERRAL_ARCHIVE_STATE.archived, label: 'Archived referrals' },
  { value: REFERRAL_ARCHIVE_STATE.all, label: 'All referrals' },
]

/**
 * Wording for the places that name the scope on its own - the mobile chip and
 * the CSV export - where a bare "All" wouldn't say all of what.
 */
export const REFERRAL_ARCHIVE_SCOPE_NAME: Record<ReferralArchiveState, string> =
  {
    active: 'Active referrals only',
    archived: 'Archived only',
    all: 'Active and archived',
  }

export const COMPANY_REGISTER: Record<string, CompanyRegister> = {
  companiesHouse: 'CompaniesHouse',
  fca: 'FCA',
  sra: 'SRA',
}

export const DEFAULT_BULK_IMPORT_CIRCUMSTANCES = 'N/A - Uploaded Referral'

// Needs to be synced with API: \App\Entity\Api\Referral::REASON_BULK_REFERRAL
export const DEFAULT_BULK_IMPORT_REASON = 'N/A - Uploaded Referral'

export const LOCKED_CLIENT_DETAILS_REASON = {
  awaitingClientConsent: 'AWAITING_CLIENT_CONSENT',
  awaitingClientCallback: 'AWAITING_CLIENT_CALLBACK',
  incompleteCommercialAgreement: 'INCOMPLETE_COMMERCIAL_AGREEMENT',
  unembeddableScheduleCallUrl: 'UNEMBEDDABLE_SCHEDULE_CALL_URL',
  awaitingClientDisclosureConsent: 'AWAITING_CLIENT_DISCLOSURE_CONSENT',
} satisfies Record<string, LockedClientDetailsReason>

export const DARK_PLACEHOLDER_STYLES: CSSProperties = {
  color: '#5D607F',
  opacity: '0.3',
}

export const INTELLIFLO_STATUS_TYPE: Record<
  IntellifloStatusType,
  IntellifloStatusType
> = {
  lead: 'lead',
  client: 'client',
  opportunity: 'opportunity',
  service_case: 'service_case',
}

export const PLANNR_CONFIG_SECTION: Record<
  PlannrConfigSection,
  PlannrConfigSection
> = {
  employeeMapping: 'employeeMapping',
  referralStatusMapping: 'referralStatusMapping',
  defaultImportStatuses: 'defaultImportStatuses',
  feeStatusMapping: 'feeStatusMapping',
  import: 'import',
}

export const PLANNR_STATUS_TYPE: Record<PlannrStatusType, PlannrStatusType> = {
  client: 'client',
  case: 'case',
}

export const REFERRAL_LINK_SCHEDULER: Record<
  ReferralLinkScheduler,
  ReferralLinkScheduler
> = {
  NYLAS: 'NYLAS',
  CALENDLY: 'CALENDLY',
}

/** @see NylasRedirectEnum in the backend */
export type NylasRedirect = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10

/** @see NylasRedirectEnum in the backend */
export const NYLAS_REDIRECT = {
  emailAndCalendarIntegration: 0,
  gettingStarted: 1,
  completeRegistrationFlow: 2,
  completeRegistrationFlowAtSignup: 3,
  emailAndCalendarIntegrationReconnected: 4,
  office365ConnectDialog: 5,
  meetingsPage: 6,
  preferences: 7,
  calendarSettings: 8,
  yourNetwork: 9,
  referralDiscovery: 10,
} satisfies Record<string, NylasRedirect>

export const TOAST_AUTOCLOSE_DELAY = 2500

export const PAYMENTS_PROCESSING_REFRESH_INTERVAL = 2500

export const PAYMENTS_RELOAD_REFRESH_INTERVAL = 8000

export const MODAL_IDS = {
  icaewConsentModal: 'icaewConsentModal',
}

export const PANEL_AGREEMENT_MESSAGE = {
  reviewAgreement: 'An agreement is pending your approval',
  chaseAgreementReview: "An agreement is pending your partner's approval",
}
export const MEETING_FORMAT: Record<MeetingFormat, MeetingFormat> = {
  video: 'video',
  phone: 'phone',
  in_person_office: 'in_person_office',
  in_person_client_home: 'in_person_client_home',
}

export const REFERRAL_LINK_TYPE: Record<ReferralLinkType, ReferralLinkType> = {
  GENERIC: 'GENERIC',
  COMPASS: 'COMPASS',
}

export const EXPECTED_COMMISSION_AMOUNT_UNSURE = -1

export const REFERRAL_STATUS_UPDATE_DISABLED_REASON = {
  NO_COMPANY: 'NO_COMPANY',
  STATUS_LOCKED: 'STATUS_LOCKED',
  COMMERCIAL_AGREEMENT_INCOMPLETE: 'COMMERCIAL_AGREEMENT_INCOMPLETE',
  INTELLIFLO_REFERRAL: 'INTELLIFLO_REFERRAL',
  PLANNR_REFERRAL: 'PLANNR_REFERRAL',
  NOT_TO_COMPANY: 'NOT_TO_COMPANY',
  OTHER: 'OTHER',
}

export const PANEL_REQUEST_APPROVAL_STATUS: Record<
  PanelRequestApprovalStatus,
  PanelRequestApprovalStatus
> = {
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
}

export const PANEL_INVITE_SOURCE: Record<PanelInviteSource, PanelInviteSource> =
  {
    DIRECT_INVITE: 'DIRECT_INVITE',
    REFERRAL_INVITE: 'REFERRAL_INVITE',
  }

export const IDENTITY_PROVIDERS = {
  USERNAME_PASSWORD: 'Username + Password',
  MICROSOFT: 'Microsoft',
  GOOGLE: 'Google',
}

export const REFERRAL_IMPORT_DIRECTION: Record<
  ReferralImportDirection,
  ReferralImportDirection
> = {
  FROM: 'FROM',
  TO: 'TO',
}

// Used by <ReferralTargetSearch />
export const REFERRAL_TARGET: Record<ReferralTarget, ReferralTarget> = {
  individual: 'individual',
  serviceArea: 'serviceArea',
  company: 'company',
  companyType: 'companyType',
}

export const COMMENT_ENTITY_ALIAS: Record<
  CommentEntityAlias,
  CommentEntityAlias
> = {
  PANEL: 'PANEL',
  INVITE_REFERRAL_PARTNER: 'INVITE_REFERRAL_PARTNER',
  REFERRAL_INVITE: 'REFERRAL_INVITE',
}

export const COMPLETE_REGISTRATION_FLOW_BOX_MAX_WIDTH = 736

export const NYLAS_SEARCH_PARAMS = {
  code: 'code',
  state: 'state',
  // Standard OAuth error fields Nylas sends back instead of a code when hosted
  // auth fails (`error_code` replaces `error_uri` for Nylas-internal failures)
  error: 'error',
  errorDescription: 'error_description',
  errorUri: 'error_uri',
  errorCode: 'error_code',
  calendarConnectionFailed: 'calendarConnectionFailed',
  hasNylasConnectionSucceeded: 'hasNylasConnectionSucceeded',
}

export const REFERRAL_CONFIRMATION_CONSENT: Record<
  ReferralCommissionConsent,
  ReferralCommissionConsent
> = {
  CONSENT: 'CONSENT',
  DO_NOT_CONSENT: 'DO_NOT_CONSENT',
}

export const REFERRAL_NOTE_TYPE = {
  referralCreated: 'referral_created',
  referralUpdated: 'referral_updated',
  referralImported: 'referral_imported',
  referralApproved: 'referral_approved',
  referralRejected: 'referral_rejected',
  referralForwarded: 'referral_forwarded',
  requestClientIntroduction: 'request_client_introduction',
  requestClientFeeConsent: 'request_client_fee_consent',
  requestClientBankDetails: 'request_client_bank_details',
  requestClientIntroductionConsent: 'request_client_introduction_consent',
  nothingToRebate: 'nothing_to_rebate',
  previouslyProvidedBankDetails: 'previously_provided_bank_details',
  initialMeetingScheduled: 'initial_meeting_scheduled',
  meetingBooked: 'meeting_booked',
  meetingRescheduled: 'meeting_rescheduled',
} satisfies Record<string, ReferralNoteType>

export const LABELS = {
  referralCircumstances: "Provide details about your client's requirements",
}

export const HELP_TEXTS = {
  dateFormat: 'Please enter the date in the format: DD/MM/YYYY',
}

export const PLACEHOLDERS = {
  referralCircumstances: 'My client needs help with...',
}

export const REFERRAL_ACCESS_DENIED_REASON: Record<
  ReferralAccessDeniedReason,
  ReferralAccessDeniedReason
> = {
  INSUFFICIENT_PRIVILEGES: 'INSUFFICIENT_PRIVILEGES',
  OTHER: 'OTHER',
}

export const REFERRALS_IMPORT_STEP: Record<
  ReferralsImportStep,
  ReferralsImportStep
> = {
  upload: 'upload',
  resolveImport: 'resolveImport',
}

export const COMPANY_DOMAIN_VERIFICATION_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
}

export const USER_ACTION_NAME: Record<UserActionName, UserActionName> = {
  visitedRqViaIntellifloStore: 'visitedRqViaIntellifloStore',
}

export const CLIENT_PARTY_TYPE: Record<ClientPartyType, ClientPartyType> = {
  PERSON: 'PERSON',
  JOINT: 'JOINT',
  CORPORATE: 'CORPORATE',
  TRUST: 'TRUST',
}

export const MAX_INTEGRATION_ERROR_ROWS_TO_SHOW = 5

export const MEDIA_OBJECT_CONTEXT: Record<
  MediaObjectContext,
  MediaObjectContext
> = {
  CLIENT_CONSENT_FILE: 'CLIENT_CONSENT_FILE',
}

export const CROSS_ORIGIN_MESSAGES = {
  // ⚠️ Note that this message shouldn't be changed because it's referenced in
  // existing iframe embed code that we've asked users to use. If we absolutely
  // need to change the message, we should consider its impact on existing users.
  requestParentUrl: 'rqRequestParentUrl',
}

export const REFERRAL_SOURCE: Record<ReferralSource, ReferralSource> = {
  INTRODUCER: 'INTRODUCER',
  DIRECT: 'DIRECT',
}

export const FILE_UPLOAD_BOX_STATE: Record<
  FileUploadBoxState,
  FileUploadBoxState
> = {
  disabled: 'disabled',
  enabled: 'enabled',
  uploading: 'uploading',
  success: 'success',
  error: 'error',
}

export const DATES = {
  // This will be 1 hour before midnight for today
  // @see https://date-fns.org/v4.1.0/docs/endOfDay
  endOfToday: endOfDay(new Date()),
}

export const EMBED_CODE_TEMPLATE_VARIANT: Record<
  EmbedCodeTemplateVariant,
  EmbedCodeTemplateVariant
> = {
  light: 'light',
  dark: 'dark',
}

export const CUSTOM_EVENT: Record<RqCustomEvent, RqCustomEvent> = {
  commercialAgreementTemplateReviewed: 'commercialAgreementTemplateReviewed',
  rateLimited: 'rateLimited',
}

export const COMMERCIAL_AGREEMENT_TEMPLATE_PLACEHOLDER: Record<
  CommercialAgreementTemplatePlaceholder,
  CommercialAgreementTemplatePlaceholder
> = {
  COMPANY_NAME: 'COMPANY_NAME',
  COMPANY_NUMBER: 'COMPANY_NUMBER',
  COMPANY_ADDRESS: 'COMPANY_ADDRESS',
}

export const CANNOT_VIEW_REFERRAL_REASON: Record<
  CannotViewReferralReason,
  CannotViewReferralReason
> = {
  NON_ADMIN: 'NON_ADMIN',
  OTHER: 'OTHER',
  DOES_NOT_BELONG_TO_REFERRAL_COMPANIES:
    'DOES_NOT_BELONG_TO_REFERRAL_COMPANIES',
}

export const REFERRAL_DIRECTION: Record<ReferralDirection, ReferralDirection> =
  {
    INCOMING: 'INCOMING',
    OUTGOING: 'OUTGOING',
  }

export const GROUP_DASHBOARD_STATS_TIME_FILTER = {
  'All time': 'all',
  '30 days': '30d',
  '90 days': '90d',
  '180 days': '180d',
  'Year to date': 'ytd',
  '365 days': '365d',
} satisfies Record<string, DashboardFilters>

export const GROUP_REFERRALS_CHART_TYPE: Record<
  DashboardReferralsChartType,
  DashboardReferralsChartType
> = {
  cumulative: 'cumulative',
  mom: 'mom',
}

export const GROUP_REFERRALS_CHART_DATA_SOURCE: Record<
  DashboardReferralsChartDataSource,
  DashboardReferralsChartDataSource
> = {
  referrals: 'referrals',
  conversions: 'conversions',
}

export const USER_CONTEXT_TYPE: Record<UserContextType, UserContextType> = {
  company: 'company',
  group: 'group',
}

export const REFERRAL_CONSENT_STAGE: Record<
  ReferralConsentStage,
  ReferralConsentStage
> = {
  INTRODUCTION: 'INTRODUCTION',
  RETROSPECTIVE: 'RETROSPECTIVE',
}

export const REFERRAL_CONSENT_LETTER_VARIANT: Record<
  ReferralConsentLetterVariant,
  ReferralConsentLetterVariant
> = {
  DEFAULT: 'DEFAULT',
  INTRA_GROUP_TRANSFER: 'INTRA_GROUP_TRANSFER',
}

export const YELLOW_BUTTON_PROPS: React.ComponentProps<typeof Button> = {
  variant: 'warning',
  size: 'lg',
  className: 'text-black px-6 w-full sm:w-auto',
  style: {
    backgroundColor: THEME_PALETTE.yellowDocuSign,
    borderColor: THEME_PALETTE.yellowDocuSign,
  },
}

export const PENDING_USER_ACTION: Record<PendingUserAction, PendingUserAction> =
  {
    REVIEW_COMMERCIAL_AGREEMENT_TEMPLATE_REVISIONS:
      'REVIEW_COMMERCIAL_AGREEMENT_TEMPLATE_REVISIONS',
  }
